import { useState, useRef, KeyboardEvent } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Send } from "lucide-react";
import { cn } from "@/lib/utils";

interface CommentFormProps {
  onSubmit: (content: string) => void;
  isSubmitting: boolean;
  initialValue?: string;
  onCancel?: () => void;
  placeholder?: string;
  submitLabel?: string;
}

export function CommentForm({
  onSubmit,
  isSubmitting,
  initialValue = "",
  onCancel,
  placeholder = "Write a comment...",
  submitLabel,
}: CommentFormProps) {
  const [content, setContent] = useState(initialValue);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isEditMode = !!onCancel;

  const handleSubmit = () => {
    const trimmed = content.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    if (!isEditMode) {
      setContent("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape" && onCancel) {
      onCancel();
    }
  };

  const nearLimit = content.length >= 4500;

  return (
    <div className={cn("rounded-lg border bg-card", isEditMode ? "p-2.5" : "p-2.5 border-border")}>
      <Textarea
        ref={textareaRef}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={isEditMode ? 2 : 2}
        className="mt-1.5 min-h-[66px] resize-none text-sm"
        disabled={isSubmitting}
        maxLength={5000}
      />
      <div className="flex items-center justify-between gap-2 pt-1.5">
        {nearLimit ? (
          <span className={cn("text-xs", content.length >= 5000 ? "text-destructive" : "text-muted-foreground")}>
            {content.length}/5000
          </span>
        ) : !isEditMode ? (
          <span className="text-xs text-muted-foreground/80">⌘↵ to send</span>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          {onCancel && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={isSubmitting}
              className="text-muted-foreground"
            >
              Cancel
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={isSubmitting || !content.trim()}
            className={cn("h-8 px-3 text-xs", !isEditMode && "gap-1.5")}
          >
            {isSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {submitLabel ?? (isEditMode ? "Save" : (
              <>
                <Send className="h-3.5 w-3.5" />
                Comment
              </>
            ))}
          </Button>
        </div>
      </div>
    </div>
  );
}
